import {
  useAppleGooglePayEmbedInCheckoutPage,
  useBillingDetailsUpdate,
  usePaymentModalFeatureFlag,
} from "@/core/splitio";
import { OrderStatus } from "@/data";
import { useAnalytics } from "@/lib/analytics/analytics";
import { useI18n } from "@/lib/i18n/i18n_provider";
import {
  useSpaceBookingPolicyQuery,
  useMembershipPoliciesQuery,
  useOrderQueryCallback,
  Order,
} from "@/offering/api";
import { OrderParams } from "@/offering/order_params";
import { PaymentMethod } from "@/offering/partner_config";
import { getCheckoutErrors } from "@/offering/template_offering_checkout_stripe_base";
import { useSubmitOrder } from "@/offering/template_offering_checkout_tranzila_base";
import { useElements, CardElement } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { useFormik } from "formik";
import { useRef, useCallback, useState } from "react";
import * as Sentry from "@sentry/browser";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "@/offering/order_params_utils";
import { useQueryString } from "@/hooks";
import { MALIN_PARTNER_UUIDS } from "@/offering/themalin/themalin_partner_config";

export interface CheckoutFormInput {
  fullName: string;
  companyName: string;
  phoneNumber: string;
  email: string;
  bookingReason?: string;
  paymentComplete: boolean;
  billingFullName?: string;
  billingPhoneNumber?: string;
  billingEmail?: string;
  billingStreetAddress: string;
  billingCity: string;
  billingState: string;
  billingPostalCode: string;
  billingCountry: string;
  billingTax: string;
  marketingConsent: boolean;
  termsChecked?: boolean;
  specialInstructions?: string;
  membershipNumber?: string;
}

interface UsePaymentProps {
  paymentMethod: PaymentMethod;
  requireBillingDetails: boolean;
  requirePayment: boolean;
  askForTax: boolean;
  requireTaxId?: boolean;
  requireBookingReason?: boolean;
  stripe: Stripe | null;
  initialValues: CheckoutFormInput;
  orderParams: OrderParams;
  partnerID: string;
  quoteID?: string;
  partnerOfferingID: string;
  offeringLocationId?: string;
  legacy__details: {
    layoutID: string;
    attendees: number;
  };
  onComplete?: (order: Order) => void;
  onError?: (msg: string) => void;
  onOrderCreated?: (order: Order) => void;
  useStripePaymentElement?: boolean;
  membership?: boolean;
}

export function usePayment(props: UsePaymentProps) {
  const {
    paymentMethod,
    requirePayment,
    requireBillingDetails,
    askForTax,
    requireTaxId,
    requireBookingReason,
    stripe,
    orderParams,
    partnerID,
    quoteID,
    partnerOfferingID,
    onComplete,
    onError,
    onOrderCreated,
    initialValues,
    legacy__details,
    membership,
    offeringLocationId,
    useStripePaymentElement = false,
  } = props;
  const elements = useElements();
  const { fbt } = useI18n();
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerID);
  const isBillingDetailsUpdateFlagEnabled = useBillingDetailsUpdate(partnerID);
  const paymentModalFeatureFlag = usePaymentModalFeatureFlag();
  const queryParams = useQueryString();
  const isEquinox =
    queryParams.fromCatalog === "equinox" &&
    MALIN_PARTNER_UUIDS.includes(partnerID);
  const membershipNumberErrorRef = useRef<string | undefined>(undefined);
  const analytics = useAnalytics();
  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    orderParams.spaceID,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const { data: membershipPolicy } = useMembershipPoliciesQuery(
    offeringLocationId && membership ? offeringLocationId : undefined
  );

  const requireTerms =
    !!spaceBookingPolicy?.terms ||
    (!!membershipPolicy?.terms && membershipPolicy.termsRequired);
  const getOrder = useOrderQueryCallback();
  const completedRef = useRef(false);

  // errors in this callback are called in submitOrder and some from errors loading stripe
  const handleError = useCallback(
    (msg: string, error?: any, order?: Order | null) => {
      Sentry.withScope((scope) => {
        if (error) {
          scope.setExtras(error);
        }
        Sentry.captureMessage(`submit order error: ${msg}`, "error");
      });

      const replay = Sentry.getReplay();
      replay?.start();
      // This is a duplicate event to above, but should make it easier to track errors on Mixpanel
      // Depending on usage, we may want to remove the above event
      analytics.event(
        "Checkout API Error",
        {
          "Error Message": msg,
          "API Error": error,
          "Order Params": orderParams,
          "Quote ID": quoteID,
          "Order ID": order?.id,
          "Require Payment": requirePayment,
          "Require Billing Details": requireBillingDetails,
          "Ask for Tax": askForTax,
          "Require Tax ID": requireTaxId,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );

      if (onError) {
        onError(msg);
      }
    },
    [
      analytics,
      onError,
      orderParams,
      quoteID,
      requirePayment,
      requireBillingDetails,
      askForTax,
      requireTaxId,
    ]
  );

  const submitOrder = useSubmitOrder({
    partnerID,
    quoteID,
    partnerOfferingID,
    requirePayment,
    onError: handleError,
  });

  const [areContactDetailsSameAsBilling, setAreContactDetailsSameAsBilling] =
    useState(false);

  const companyNameRequired = askForTax && !!(requirePayment || membership);

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    submitForm,
    isSubmitting,
    setFieldTouched,
  } = useFormik<CheckoutFormInput>({
    initialValues,
    validate: (v) =>
      getCheckoutErrors(
        v,
        requireBillingDetails,
        !areContactDetailsSameAsBilling &&
          askForTax &&
          isBillingDetailsUpdateFlagEnabled,
        askForTax,
        paymentMethod === PaymentMethod.Stripe &&
          requirePayment &&
          isGoogleApplePayEmbedInCheckoutPage &&
          !paymentModalFeatureFlag,
        requireTerms,
        companyNameRequired,
        fbt,
        requireBookingReason,
        isEquinox,
        membershipNumberErrorRef.current,
        requireTaxId
      ),
    onSubmit: async (v) => {
      if (
        paymentMethod === PaymentMethod.Stripe &&
        !useStripePaymentElement &&
        (!stripe || !elements)
      ) {
        handleError(fbt("Stripe was not loaded"));
        return;
      }

      const values = { ...v };

      if (
        askForTax &&
        areContactDetailsSameAsBilling &&
        isBillingDetailsUpdateFlagEnabled
      ) {
        values.billingFullName = values.fullName;
        values.billingPhoneNumber = values.phoneNumber;
        values.billingEmail = values.email;
      }

      if (!isEquinox) {
        values.membershipNumber = undefined;
      }

      analytics.event(
        "Checkout Submit Order Request",
        {
          "Quote ID": quoteID,
          Values: values,
          "Order Params": orderParams,
          "Legacy Details": legacy__details,
        },
        { onlyProviders: ["mixpanel", "customerio"] }
      );

      const order = await submitOrder(values, orderParams, legacy__details);

      // errors are already handled in submitOrder
      if (!order) {
        return;
      }

      // Tranzila does not handle payment here, it only needs the created order for the moment
      if (paymentMethod === PaymentMethod.Tranzila) {
        if (onOrderCreated) {
          onOrderCreated(order);
        }
        return;
      }

      if (!requirePayment) {
        if (useStripePaymentElement && !!onOrderCreated && membership) {
          onOrderCreated(order);
          return;
        }

        if (onComplete) {
          onComplete(order);
        }
        return;
      }

      if (useStripePaymentElement && onOrderCreated) {
        onOrderCreated(order);
        return;
      }

      if (!stripe || !elements) {
        handleError(fbt("Stripe was not loaded"));
        return;
      }

      const card = elements.getElement(CardElement);

      if (!card) {
        handleError(fbt("Card component not found"));
        return;
      }

      const result = await stripe.confirmCardPayment(order.stripeClientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: values.fullName,
          },
        },
      });

      if (result.error) {
        handleError(
          result.error.message || "Payment unsuccessful",
          result.error
        );
      } else if (result.paymentIntent) {
        // The payment has been processed!
        if (result.paymentIntent.status === "succeeded") {
          await new Promise<void>((resolve) => {
            let count = 0;

            // Start polling
            const interval = setInterval(async () => {
              // poll for maximum 60 seconds
              if (order && count < 60) {
                const polledOrder = await getOrder(order.id);
                count++;
                if (
                  polledOrder.status === OrderStatus.Paid &&
                  polledOrder.orderItems.length > 0
                ) {
                  clearInterval(interval);
                  // Even though we clear interval, async nature may still call this function
                  // multiple times as response is returned
                  if (onComplete && completedRef.current === false) {
                    completedRef.current = true;
                    onComplete(order);
                  }
                  resolve();
                }
              } else {
                clearInterval(interval);
                handleError(fbt("Payment unsuccessful"), { order });
                resolve();
              }
            }, 1000);
          });
        }
      }
    },
  });

  membershipNumberErrorRef.current = errors.membershipNumber;

  return {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    submit: submitForm,
    submitting: isSubmitting,
    onCheckContactDetailsSameAsBilling: setAreContactDetailsSameAsBilling,
  };
}
